import { useEffect, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  REDO_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import { useTranslation } from 'react-i18next';

import ArrowClockwiseIcon from 'assets/images/icons/arrow-clockwise.svg?react';
import ArrowCounterclockwiseIcon from 'assets/images/icons/arrow-counterclockwise.svg?react';
import Button from 'components/button/Button';
import { RichTextEditorToolbarPluginProps } from 'components/editor/RichTextEditor.types';

interface UndoRedoButtonsProps {
  disabled: boolean;
  toolbarSettings: RichTextEditorToolbarPluginProps['toolbarSettings'];
}

const UndoRedoButtons = ({
  disabled,
  toolbarSettings: { features },
}: UndoRedoButtonsProps) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor]);

  return (
    <>
      {features?.undo && (
        <Button
          aria-label={t('form.editor.buttons.undo')}
          className="toolbar-item spaced"
          data-testid="toolbar-button-undo"
          disabled={!canUndo || disabled}
          onClick={() => {
            editor.dispatchCommand(UNDO_COMMAND, undefined);
          }}
        >
          <ArrowCounterclockwiseIcon />
        </Button>
      )}
      {features?.redo && (
        <Button
          aria-label={t('form.editor.buttons.redo')}
          className="toolbar-item"
          data-testid="toolbar-button-redo"
          disabled={!canRedo || disabled}
          onClick={() => {
            editor.dispatchCommand(REDO_COMMAND, undefined);
          }}
        >
          <ArrowClockwiseIcon />
        </Button>
      )}
    </>
  );
};

export default UndoRedoButtons;
